<template>
  <div class="relation-patient-doctor">

    <!-- SUMMARY CARDS -->
    <div class="summary-cards-container">
      <ul class="summary-cards" v-if="summary">
        <li class="summary-card">
          <span class="card-value">{{ totalPatients }}</span>
          <span class="card-label">Total Pacienți</span>
        </li>
        <li class="summary-card">
          <span class="card-value">{{ summary.patientsWithSingleDoctor }}</span>
          <span class="card-label">Pacienți cu un singur medic</span>
        </li>
        <li class="summary-card">
          <span class="card-value">{{ summary.patientsWithMultipleDoctors }}</span>
          <span class="card-label">Pacienți cu mai mulți medici</span>
        </li>
        <li class="summary-card">
          <span class="card-value">{{ summary.loyalPatients }}</span>
          <span class="card-label">Pacienți Loiali</span>
        </li>
        <li class="summary-card">
          <span class="card-value">{{ summary.avgContinuityScore }}</span>
          <span class="card-label">Scor Mediu Continuitate</span>
        </li>
        <li class="summary-card">
          <span class="card-value">{{ summary.avgDoctorsPerPatient }}</span>
          <span class="card-label">Medie Medici / Pacient</span>
        </li>
        <li class="summary-card">
          <span class="card-value">{{ summary.avgRelationshipMonths }}</span>
          <span class="card-label">Durată Medie Relație (Luni)</span>
        </li>
      </ul>

      <div class="action-bar">
        <button class="download-btn" @click="$emit('download')" :disabled="isDownloading || isLoading">
          <span v-if="!isDownloading">📥 Descarcă Excel</span>
          <span v-else class="btn-spinner"></span>
        </button>
      </div>
    </div>

    <!-- DOCTORS SUMMARY TABLE -->
    <template v-if="doctorsSummary && doctorsSummary.topDoctors && doctorsSummary.topDoctors.length">
      <h3 class="section-title">Top Medici ({{ doctorsSummary.totalDoctors }} total)</h3>
      <div class="table-wrapper" style="margin-bottom: 32px;">
        <table class="table">
          <thead>
          <tr>
            <th>#</th>
            <th>Medic</th>
            <th>Total Pacienți</th>
            <th>Pacienți Loiali</th>
            <th>Rată Loialitate</th>
            <th>Scor Mediu Continuitate</th>
            <th>Medie Tratamente / Pacient</th>
            <th>Durată Medie Relație (Luni)</th>
            <th>Total Tratamente</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(doc, index) in doctorsSummary.topDoctors" :key="doc.doctorName">
            <td>{{ index + 1 }}</td>
            <td>{{ doc.doctorName }}</td>
            <td>{{ doc.totalPatients }}</td>
            <td>{{ doc.loyalPatients }}</td>
            <td>{{ doc.loyaltyRate }}</td>
            <td>{{ doc.avgContinuityScore }}</td>
            <td>{{ doc.avgTreatmentsPerPatient }}</td>
            <td>{{ doc.avgRelationshipMonths }}</td>
            <td>{{ doc.totalTreatments }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- PATIENTS TABLE -->
    <h3 class="section-title">Pacienți</h3>
    <div class="table-wrapper" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="table-loader-overlay">
        <div class="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>
      <table class="table">
        <thead>
        <tr>
          <th>#</th>
          <th>Nume</th>
          <th>Prenume</th>
          <th>Oraș</th>
          <th>Total Tratamente</th>
          <th>Nr. Medici</th>
          <th>Medic Principal</th>
          <th>Tratamente Medic Principal</th>
          <th>Scor Continuitate</th>
          <th>Loial</th>
          <th>Un Singur Medic</th>
          <th>Prima Vizită</th>
          <th>Ultima Vizită</th>
          <th>Durată Relație (Zile)</th>
          <th>Durată Relație (Luni)</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="patients.length === 0">
          <td colspan="15" class="no-data">Nu există date disponibile</td>
        </tr>
        <tr v-else v-for="(item, index) in patients" :key="item._id">
          <td>{{ index + 1 }}</td>
          <td>{{ item.nume }}</td>
          <td>{{ item.prenume }}</td>
          <td>{{ item.oras }}</td>
          <td>{{ item.totalTreatments }}</td>
          <td>{{ item.numberOfDoctors }}</td>
          <td>{{ item.primaryDoctor }}</td>
          <td>{{ item.primaryDoctorCount }}</td>
          <td>{{ item.continuityScore }}</td>
          <td>
              <span class="urgency-badge" :class="item.isLoyal ? 'urgency-low' : 'urgency-high'">
                {{ item.isLoyal ? 'Da' : 'Nu' }}
              </span>
          </td>
          <td>{{ item.hasSingleDoctor ? 'Da' : 'Nu' }}</td>
          <td>{{ item.firstVisit }}</td>
          <td>{{ item.lastVisit }}</td>
          <td>{{ item.relationshipDays }}</td>
          <td>{{ item.relationshipMonths }}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RelationPatientDoctor',

  props: {
    totalPatients: { type: Number, default: 0 },
    summary: { type: Object, default: () => null },
    doctorsSummary: { type: Object, default: () => null },
    patients: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    isDownloading: { type: Boolean, default: false }
  },

  emits: ['download']
}
</script>

<style lang="less">
@import '../assets/styles/var.less';

.relation-patient-doctor {

  .section-title {
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8e8e93;
    margin: 28px 0 10px;
  }

  .summary-cards-container {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 28px;
  }

  .summary-cards {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
    margin: 0;
    padding: 0;
    list-style: none;

    .summary-card {
      display: flex;
      flex-direction: column;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.06);
      border-radius: 12px;
      padding: 14px 16px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      .card-value {
        font-size: 22px;
        font-weight: 700;
        color: #1c1c1e;
        letter-spacing: -0.02em;
        line-height: 1.2;
        margin-bottom: 4px;
      }

      .card-label {
        font-size: 11px;
        font-weight: 500;
        color: #8e8e93;
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
    }
  }

  .action-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    margin-bottom: 0 !important;
  }

  .download-btn {
    background: #34c759;
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #2eb350;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
